import React from "react";
import { motion } from "framer-motion";
import { Building2, Factory, Building, ChevronRight, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { IMAGES } from "@/assets/images";
import { ROUTE_PATHS } from "@/lib/index";
import { Button } from "@/components/ui/button";
import { springPresets, fadeInUp, staggerContainer, staggerItem } from "@/lib/motion";

export default function ProductsEn() {
  const productCategoriesEn = [
    {
      title: "Architectural Systems",
      description: "Comprehensive aluminum solutions for modern architecture and construction projects.",
      icon: <Building2 className="w-12 h-12 text-primary" />,
      image: IMAGES.MINIMAL_SURME_SISTEMLER_6_299,
      features: [
        "Thermal Insulated Joinery",
        "Minimal Sliding Systems",
        "Curtain Wall Systems",
        "Office Partition Systems",
        "Sun Breakers",
        "Skylight Systems",
        "Insect Screen Systems",
        "Pergola Systems",
        "Bioclimatic Pergola",
        "Curtain Profiles",
        "Architectural Detail Solution Profiles",
        "Lighting Profiles"
      ],
      link: ROUTE_PATHS.ARCHITECTURAL_SYSTEMS_EN
    },
    {
      title: "Facade and Joinery Systems",
      description: "Advanced facade solutions that meet the demands of modern architecture with aesthetic and functional design.",
      icon: <Building className="w-12 h-12 text-primary" />,
      image: IMAGES.ALUMINUM_PRODUCTS_3,
      features: [
        "Advanced Technology Curtain Wall Systems",
        "Structural Glazing Systems",
        "Curtain Wall Systems",
        "Composite Panel Systems"
      ],
      link: ROUTE_PATHS.FACADE_SYSTEMS_EN
    },
    {
      title: "Industrial Profiles",
      description: "High-strength aluminum profiles for industrial applications and manufacturing sectors.",
      icon: <Factory className="w-12 h-12 text-primary" />,
      image: IMAGES.OTOMOTIV_305,
      features: [
        "Structural Profiles",
        "Machine Frame Systems",
        "Conveyor Systems",
        "Heat Sink Profiles",
        "LED Lighting Profiles",
        "Solar Panel Frames",
        "Custom Industrial Solutions"
      ],
      link: ROUTE_PATHS.PRODUCTS_INDUSTRIAL_EN
    }
  ];

  return (
    <div className="flex flex-col w-full">
      {/* Hero Section */}
      <section className="relative py-24 bg-primary text-primary-foreground overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <img 
            src={IMAGES.HERO_INDUSTRIAL_3} 
            alt="Products" 
            className="w-full h-full object-cover"
          />
        </div>
        <div className="container relative z-10 mx-auto px-4 text-center">
          <motion.h1 
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-5xl font-bold mb-6"
          >
            Our Products
          </motion.h1>
          <motion.p 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="text-xl max-w-3xl mx-auto leading-relaxed"
          >
            Comprehensive aluminum solutions for architectural and industrial applications with high quality and precision manufacturing.
          </motion.p>
        </div>
      </section>

      {/* Product Categories */}
      <section id="architectural-systems" className="py-24 bg-background">
        <div className="container mx-auto px-4">
          <motion.div
            variants={staggerContainer}
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <motion.h2 variants={fadeInUp} className="text-4xl font-bold mb-6">
              Product Categories
            </motion.h2>
            <motion.p variants={fadeInUp} className="text-xl text-muted-foreground max-w-3xl mx-auto">
              We offer specialized aluminum solutions for different sectors with our wide product range and customization capabilities.
            </motion.p>
          </motion.div>

          <div className="space-y-24">
            {productCategoriesEn.map((category, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8, delay: index * 0.2 }}
                className={`grid grid-cols-1 lg:grid-cols-2 gap-16 items-center ${
                  index % 2 !== 0 ? 'lg:grid-flow-col-dense' : ''
                }`}
              >
                <div className={index % 2 !== 0 ? 'lg:col-start-2' : ''}>
                  <div className="mb-6">
                    {category.icon}
                  </div>
                  <h3 className="text-3xl font-bold mb-6 text-primary">{category.title}</h3>
                  <p className="text-lg text-muted-foreground mb-8 leading-relaxed">
                    {category.description}
                  </p>
                  
                  <div className="mb-8">
                    <h4 className="text-lg font-semibold mb-4">Product Range:</h4>
                    <ul className="grid grid-cols-1 md:grid-cols-2 gap-2">
                      {category.features.map((feature, featureIndex) => (
                        <li key={featureIndex} className="flex items-center gap-2 text-muted-foreground">
                          <ChevronRight className="w-4 h-4 text-primary" />
                          {feature}
                        </li>
                      ))}
                    </ul>
                  </div>

                  <Button asChild className="text-lg px-6 py-3">
                    <Link to={category.link}>
                      View Details <ArrowRight className="ml-2 w-5 h-5" />
                    </Link>
                  </Button>
                </div>
                
                <div className={index % 2 !== 0 ? 'lg:col-start-1 lg:row-start-1' : ''}>
                  <div className="relative rounded-2xl overflow-hidden shadow-2xl aspect-video">
                    <img 
                      src={category.image} 
                      alt={category.title} 
                      className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Technical Specifications */}
      <section id="industrial-profiles" className="py-24 bg-muted/30">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-bold mb-6">Technical Capabilities</h2>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
              Our production capabilities and technical specifications that ensure high-quality aluminum solutions.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              {
                title: "Alloy Types",
                specs: ["6000 Series", "7000 Series", "Custom Alloys", "High Strength"]
              },
              {
                title: "Profile Sizes",
                specs: ["4\" - 8\" Range", "Complex Geometries", "Thin Wall Sections", "Heavy Sections"]
              },
              {
                title: "Surface Treatments",
                specs: ["Anodizing", "Powder Coating", "PVDF Coating", "Wood Grain Finish"]
              },
              {
                title: "Quality Standards",
                specs: ["ISO 9001", "EN Standards", "ASTM Compliance", "Custom Testing"]
              }
            ].map((spec, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8, delay: index * 0.1 }}
                className="bg-card p-6 rounded-lg border"
              >
                <h3 className="text-lg font-semibold mb-4 text-primary">{spec.title}</h3>
                <ul className="space-y-2">
                  {spec.specs.map((item, itemIndex) => (
                    <li key={itemIndex} className="flex items-center gap-2 text-sm text-muted-foreground">
                      <ChevronRight className="w-3 h-3 text-primary" />
                      {item}
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-primary text-primary-foreground">
        <div className="container mx-auto px-4 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="max-w-3xl mx-auto"
          >
            <h2 className="text-4xl font-bold mb-6">
              Need Custom Solutions?
            </h2>
            <p className="text-xl mb-8 text-primary-foreground/90">
              Our engineering team can develop custom aluminum solutions for your specific requirements. 
              Contact us to discuss your project needs.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button asChild size="lg" variant="secondary" className="text-lg px-8 py-6">
                <Link to={ROUTE_PATHS.CONTACT_EN}>
                  Request Quote <ArrowRight className="ml-2 w-5 h-5" />
                </Link>
              </Button>
              <Button asChild size="lg" variant="outline" className="text-lg px-8 py-6 border-white/30 text-white hover:bg-white hover:text-primary">
                <Link to={ROUTE_PATHS.PROFILE_GUIDE_EN}>
                  Technical Guide
                </Link>
              </Button>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}